import { useState } from "react";
import { useModuleBEM } from "@b2bportal/core-themes";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import type { Listing } from "@b2bportal/homes-api";
import { DateTimeFormatStyle, useI18nContext } from "@hopper-b2b/i18n";
import { HotelShopSection } from "@tenant/hotels";
import { Button, ButtonTypeEnum } from "@tenant/ui";
import clsx from "clsx";
import {
  formatReviewDate,
  formatScore,
  getVerificationSource,
  IVerificationSourceEnum,
  normalizeOverallScore,
  normalizeReviewScore,
} from "./helpers";
import styles from "./HomeCustomerReviews.module.scss";
import { useHomeCustomerReviews } from "./hooks";

const RECENT_REVIEWS_COUNT = 3;
const ALL_REVIEWS_COUNT = 6;

export const HomeCustomerReviews = ({
  listing,
  className,
}: { listing: Listing } & PlatformComponentProps) => {
  const [showAllReviews, setShowAllReviews] = useState(false);

  const [block, cn] = useModuleBEM(styles, "HomeCustomerReviews");
  const { t, formatDateTime } = useI18nContext();

  const overAllScore = normalizeOverallScore(
    listing.content.userRating?.score ?? 0
  );

  const reviews = listing.reviews;

  const hasMoreReviews = reviews.length > RECENT_REVIEWS_COUNT;

  const allVrboReviews = reviews.every((review) => {
    return (
      review.verificationSource != null &&
      getVerificationSource(review.verificationSource.text) ===
        IVerificationSourceEnum.VRBO
    );
  });

  if (reviews.length === 0 && overAllScore === 0) return null;

  return (
    <HotelShopSection className={clsx(block, className)}>
      <div className={cn("title-container")}>
        <p className={cn("title")}>{t("customerReviews")}</p>
        {allVrboReviews && (
          <div className={cn("logo-container")}>
            <span aria-label={t("core-hotels.homes.vrboAlt")}>
              {t("core-hotels.homes.poweredBy")}
            </span>
            <img
              alt=""
              height={28}
              src="https://cdn.static.hopper.com/homes/VrboLogoDarkBlue.png"
            />
          </div>
        )}
      </div>
      <HomeUserRating score={overAllScore} size="md" />
      <div className={cn("review-list")}>
        {reviews
          .slice(0, showAllReviews ? ALL_REVIEWS_COUNT : RECENT_REVIEWS_COUNT)
          .map((review) => {
            const date = formatReviewDate(review.submittedDate);

            const formattedDate = date
              ? formatDateTime(date, DateTimeFormatStyle.YearShortMonth)
              : "";

            return (
              <div key={review.authorName} className={cn("review-item")}>
                <HomeUserRating
                  score={normalizeReviewScore(review.overallScore)}
                  size="sm"
                />
                <p className={cn("comment")}>{review.comments}</p>
                <p
                  className={cn("author")}
                >{`${review.authorName}, ${formattedDate}`}</p>
              </div>
            );
          })}
      </div>
      {hasMoreReviews && (
        <Button
          buttonType={ButtonTypeEnum.Link}
          onClick={() => setShowAllReviews((prevState) => !prevState)}
        >
          {showAllReviews
            ? t("core-hotels.homes.viewLess")
            : t("core-hotels.homes.moreReviews")}
        </Button>
      )}
    </HotelShopSection>
  );
};

export const HomeUserRating = ({
  score,
  size,
  className,
}: {
  score: number;
  size: "sm" | "md";
  className?: string;
}) => {
  const [, cn] = useModuleBEM(styles, "HomeCustomerReviews");
  const { locale } = useI18nContext();

  const { ratingLabel } = useHomeCustomerReviews(score);

  if (score === 0) return null;

  return (
    <div className={clsx(cn("score-container"), className)}>
      <p className={cn("score-label", [size])}>{ratingLabel}</p>
      <p className={cn("score-value", [size])}>{formatScore(score, locale)}</p>
    </div>
  );
};
